import type { ReactNode } from 'react';
import { Box, Divider, Group, Stack, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

import { AmountBasisPill } from '@/features/board/components/AmountBasisPill';
import { PanelInfoIcon } from '@/features/board/components/PanelInfoIcon';

interface ExecutiveKpiCardProps {
  label: string;
  info: ReactNode;
  value: string;
  subtitle: string;
  progress: number;
  progressCaption: string;
  children?: ReactNode;
  warning?: boolean;
}

export const ExecutiveKpiCard = ({
  label,
  info,
  value,
  subtitle,
  progress,
  progressCaption,
  children,
  warning
}: ExecutiveKpiCardProps) => {
  const isMobile = useMediaQuery('(max-width: 48em)');
  return (
    <Stack
      gap={isMobile ? 10 : 12}
      p={isMobile ? 16 : 18}
      style={{
        height: '100%',
        borderRadius: 12,
        backgroundColor: 'var(--color-bg-surface)',
        border: warning
          ? '1.5px solid var(--color-status-warning)'
          : '1px solid var(--color-border-default)'
      }}
    >
      <Group gap={6} wrap="nowrap">
        <Text
          fz={12}
          fw={500}
          lh={1.3}
          style={{ flex: 1, minWidth: 0, color: 'var(--color-text-secondary)' }}
        >
          {label}
        </Text>
        <Box style={{ flexShrink: 0, display: 'flex' }}>
          <PanelInfoIcon label={info} width={280} title={label} />
        </Box>
      </Group>
      <Stack gap={6}>
        <Text fz={24} fw={700} lh={1.2} style={{ color: 'var(--color-text-primary)' }}>
          {value}
        </Text>
        <AmountBasisPill label={subtitle} />
      </Stack>
      <Stack gap={6}>
        <Box
          style={{
            height: 6,
            borderRadius: 3,
            overflow: 'hidden',
            backgroundColor: 'var(--color-border-soft)'
          }}
        >
          <Box
            style={{
              height: '100%',
              width: `${Math.max(0, Math.min(100, progress))}%`,
              borderRadius: 3,
              backgroundColor: 'var(--color-accent-primary)'
            }}
          />
        </Box>
        <Text fz={11} lh={1.3} style={{ color: 'var(--color-text-muted)' }}>
          {progressCaption}
        </Text>
      </Stack>
      <Divider color="var(--color-border-soft)" />
      <Stack gap={6}>{children}</Stack>
    </Stack>
  );
};

export const KpiRow = ({ label, value }: { label: string; value: string }) => (
  <Group justify="space-between" wrap="nowrap" gap={8}>
    <Text fz={11} lh={1.3} style={{ flex: 1, minWidth: 0, color: 'var(--color-text-secondary)' }}>
      {label}
    </Text>
    <Text
      fz={12}
      fw={600}
      lh={1.3}
      ta="right"
      style={{ flexShrink: 0, whiteSpace: 'nowrap', color: 'var(--color-text-primary)' }}
    >
      {value}
    </Text>
  </Group>
);
